import { openDrawer } from 'Services/Drawer';
import openAgreement from 'App/helpers/openSubscriptionHelper';
import { openEditOrder } from 'App/components/EditOrder';
import { openEditAppointment } from 'Components/Modals/Appointment/EditAppointment';
import { shouldIgnoreRepeatOpen } from './entityOpenGuard';
import AppService from 'App/legacy/AppService';

const getCustomerId = (): string => {
	const id = AppService.getCustomerId();
	return id != null ? String(id) : '';
};

const openEntityInNewTab = (stateName: string, id: number): boolean => {
	const href = Tools.$state?.href?.(stateName, { id, customerId: getCustomerId() });
	if (!href) return false;
	const absolute = href.startsWith('http') ? href : `${window.location.origin}${href}`;
	window.open(absolute, '_blank', 'noopener');
	return true;
};

export type CrmEntityOpts = {
	/** When the entity is `activity`, the activityType determines whether
	 *  the entity is a To-do, a Phone call, or a regular Activity. Each has
	 *  its own editing surface (drawer/modal). */
	activityType?: string | null;
	/** When the entity is a deal, the probability decides whether it reads as
	 *  an order or an opportunity. Resolve it from a write payload with
	 *  `orderProbabilityOf`. */
	probability?: number | null;
};

/**
 * The probability behind an agent write payload for a deal. The tool schema
 * requires `stage` but only asks for `probability` when the deal is meant to be
 * an opportunity, so an explicit probability wins and the chosen stage's own
 * probability is the fallback. Returns null when neither is knowable — the
 * caller then keeps the neutral "order" wording rather than guessing.
 */
export const orderProbabilityOf = (data: Record<string, unknown> | null | undefined): number | null => {
	if (!data) return null;
	if (typeof data.probability === 'number') return data.probability;
	const stage = data.stage;
	const stageId =
		typeof stage === 'number'
			? stage
			: stage && typeof stage === 'object' && typeof (stage as { id?: unknown }).id === 'number'
			? (stage as { id: number }).id
			: null;
	if (stageId == null) return null;
	// getStages is served from the already-loaded app state, so this is a
	// synchronous cache read. Ignore access rights: we are naming a record the
	// agent picked, not offering the stage as a choice.
	const stages = AppService.getStages('all', true) ?? [];
	return stages.find(s => s.id === stageId)?.probability ?? null;
};

const normalizeActivitySubtype = (raw: string | null | undefined): 'todo' | 'phonecall' | 'activity' => {
	const s = (raw ?? '')
		.trim()
		.toLowerCase()
		.replace(/[\s_-]/g, '');
	if (s === 'todo') return 'todo';
	if (s === 'phonecall') return 'phonecall';
	return 'activity';
};

/**
 * Dispatches a click on an "open this entity" link from chat surfaces
 * (WriteCard success state, mention chips in MessageBlock, etc.) to the
 * right modal/drawer/route. Returns false when the entity type is not
 * routable so the caller can hide the link.
 */
export function openCrmEntity(entityType: string, id: number, opts?: CrmEntityOpts): boolean {
	// Swallow a spammed click rather than stacking another modal/tab. Still
	// reports true: the entity IS routable, so the caller must keep the link.
	if (shouldIgnoreRepeatOpen(entityType, id)) return true;
	switch (entityType.toLowerCase()) {
		case 'client':
		case 'account':
		case 'company':
			return openEntityInNewTab('account.dashboard', id);
		case 'contact':
		case 'person':
			return openEntityInNewTab('contact.dashboard', id);
		case 'segment':
			return openEntityInNewTab('segment', id);
		case 'order':
		case 'deal':
		case 'opportunity':
			openEditOrder({ id });
			return true;
		case 'todo':
			openDrawer('EditTodo', { todo: { id } });
			return true;
		case 'phonecall':
			openDrawer('EditPhonecall', { activity: { id } });
			return true;
		case 'activity':
		case 'task': {
			const subtype = normalizeActivitySubtype(opts?.activityType);
			if (subtype === 'todo') {
				openDrawer('EditTodo', { todo: { id } });
				return true;
			}
			if (subtype === 'phonecall') {
				openDrawer('EditPhonecall', { activity: { id } });
				return true;
			}
			Tools.$upModal.open('editActivity', { id });
			return true;
		}
		case 'appointment':
		case 'meeting':
			openEditAppointment({ id });
			return true;
		case 'agreement':
			openAgreement({ agreementId: id });
			return true;
		case 'ticket':
			openDrawer('EditTicket', { ticketId: id });
			return true;
		case 'projectplan':
			openDrawer('EditProjectPlan', { projectPlanId: id });
			return true;
		case 'lead':
			Tools.$upModal.open('editLead', { id, customerId: getCustomerId() });
			return true;
		// `project` is the entity the UI calls Campaign / Kampanj — its dashboard
		// is the only surface that shows one.
		case 'project':
		case 'campaign':
			return openEntityInNewTab('campaign.dashboard', id);
		default:
			return false;
	}
}

export function canOpenCrmEntity(entityType: string): boolean {
	switch (entityType.toLowerCase()) {
		case 'client':
		case 'account':
		case 'company':
		case 'contact':
		case 'person':
		case 'segment':
		case 'order':
		case 'deal':
		case 'opportunity':
		case 'activity':
		case 'task':
		case 'appointment':
		case 'meeting':
		case 'todo':
		case 'phonecall':
		case 'agreement':
		case 'ticket':
		case 'projectplan':
		case 'lead':
		case 'project':
		case 'campaign':
			return true;
		default:
			return false;
	}
}

/** Maps an entity alias to the lang-key suffix that names it. Keep aligned
 *  with canOpenCrmEntity — every routable entity must produce a label. */
export function crmEntityLabelKey(entityType: string, opts?: CrmEntityOpts): string | null {
	switch (entityType.toLowerCase()) {
		case 'client':
		case 'account':
		case 'company':
			return 'default.client';
		case 'contact':
		case 'person':
			return 'default.contact';
		case 'segment':
			return 'default.segment';
		case 'order':
		case 'deal':
		case 'opportunity': {
			// A deal is only a "försäljning"/order once it is won. Anything the
			// customer is still working — the whole open pipeline — is an
			// opportunity, matching orderTypeOf in ui-3/src/lib/orderWrite.ts.
			const { probability } = opts ?? {};
			return probability != null && probability !== 100 ? 'default.opportunity' : 'default.order';
		}
		case 'activity':
		case 'task': {
			const subtype = normalizeActivitySubtype(opts?.activityType);
			if (subtype === 'todo') return 'default.todo';
			if (subtype === 'phonecall') return 'default.phonecall';
			return 'default.activity';
		}
		case 'appointment':
		case 'meeting':
			return 'default.appointment';
		case 'todo':
			return 'default.todo';
		case 'phonecall':
			return 'default.phonecall';
		case 'agreement':
			return 'default.agreement';
		case 'ticket':
			return 'default.ticket';
		case 'projectplan':
			return 'default.projectPlan';
		case 'lead':
			return 'default.lead';
		case 'project':
		case 'campaign':
			return 'default.project';
		default:
			return null;
	}
}
